// Google Analytics Configuration
export const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID || '';

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Helper function to safely call gtag
const gtag = (...args: any[]) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
};

// In development every event payload gets debug_mode:true so it surfaces in
// GA4 DebugView instead of being silently discarded as localhost traffic.
const devFlag: Record<string, boolean> =
  process.env.NODE_ENV === 'development' ? { debug_mode: true } : {};

// ─── Initialisation ──────────────────────────────────────────────────────────

// Called by AnalyticsContext on mount.
// In development: re-configures the property with debug_mode:true so all events
// appear immediately in GA4 → Admin → DebugView (and the GA4 Realtime report).
// In production: send_page_view stays false; React handles every page view.
export const initGA = () => {
  if (!GA_TRACKING_ID) return;

  if (process.env.NODE_ENV === 'development') {
    // debug_mode routes events to GA4 DebugView — visible within seconds.
    gtag('config', GA_TRACKING_ID, {
      send_page_view: false,
      debug_mode: true,
    });
    console.log('[GA4] Debug mode enabled — view events at:\nhttps://analytics.google.com/analytics/web/#/a.../realtime\nor Admin → DebugView');
  }
};

// ─── Page View ───────────────────────────────────────────────────────────────

// Track a page view (called by usePageTracking on every route change)
export const trackPageView = (url: string, title?: string) => {
  if (!GA_TRACKING_ID) return;
  gtag('event', 'page_view', {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.origin + url,
    ...devFlag,
  });
};

// Enhanced page view – includes UTM attribution + optional load time
export const trackEnhancedPageView = (url: string, title?: string, loadTime?: number) => {
  if (!GA_TRACKING_ID) return;

  const utmParams = getStoredUTMParameters();

  gtag('event', 'page_view', {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.origin + url,
    ...utmParams,
    ...devFlag,
  });

  if (loadTime && loadTime > 0) {
    gtag('event', 'timing_complete', {
      name: 'page_load',
      value: loadTime,
      event_category: 'performance',
      ...devFlag,
    });
  }
};

// ─── Generic Events ──────────────────────────────────────────────────────────

// Low-level event helper
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  extraParams?: Record<string, any>
) => {
  if (!GA_TRACKING_ID) return;
  gtag('event', action, {
    event_category: category,
    event_label: label,
    value,
    ...extraParams,
    ...devFlag,
  });
};

// Event helper that automatically includes stored UTM params
export const trackEventWithUTM = (
  action: string,
  category: string,
  label?: string,
  additionalParams?: Record<string, any>
) => {
  if (!GA_TRACKING_ID) return;
  const utmParams = getStoredUTMParameters();
  gtag('event', action, {
    event_category: category,
    event_label: label,
    ...utmParams,
    ...additionalParams,
    ...devFlag,
  });
};

// ─── UTM Helpers ─────────────────────────────────────────────────────────────

// Read UTM params from the current URL
export const getUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || 'direct',
    utm_medium: urlParams.get('utm_medium') || 'none',
    utm_campaign: urlParams.get('utm_campaign') || 'none',
    utm_term: urlParams.get('utm_term') || 'none',
    utm_content: urlParams.get('utm_content') || 'none',
  };
};

// Persist UTM params for the whole session
export const storeUTMParameters = () => {
  const utmParams = getUTMParameters();
  const hasUTM = Object.values(utmParams).some(v => v !== 'direct' && v !== 'none');
  if (hasUTM) {
    sessionStorage.setItem('utm_parameters', JSON.stringify(utmParams));
  }
};

// Retrieve persisted UTM params (fall back to current URL)
export const getStoredUTMParameters = () => {
  const stored = sessionStorage.getItem('utm_parameters');
  if (stored) {
    return JSON.parse(stored);
  }
  return getUTMParameters();
};

// ─── Conversion Events ───────────────────────────────────────────────────────

export const trackConversion = (
  conversionAction: string,
  value?: number,
  additionalParams?: Record<string, any>
) => {
  if (!GA_TRACKING_ID) return;
  const utmParams = getStoredUTMParameters();
  gtag('event', conversionAction, {
    event_category: 'conversion',
    value,
    currency: 'INR',
    ...utmParams,
    ...additionalParams,
    ...devFlag,
  });
};

// ─── GA4 Enhanced E-commerce ──────────────────────────────────────────────────

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;  // service category, e.g. "Shiv Pujan"
  item_variant?: string;   // plan name, e.g. "Premium"
  price?: number;
  quantity?: number;
  currency?: string;
}

/**
 * view_item — when user lands on a service / sub-service detail page
 */
export const trackViewItem = (item: GA4Item) => {
  if (!GA_TRACKING_ID) return;
  gtag('event', 'view_item', {
    currency: 'INR',
    value: item.price ?? 0,
    items: [{ ...item, quantity: item.quantity ?? 1 }],
    ...devFlag,
  });
};

/**
 * add_to_cart — when user adds a service to cart or wishlist
 */
export const trackAddToCart = (item: GA4Item) => {
  if (!GA_TRACKING_ID) return;
  gtag('event', 'add_to_cart', {
    currency: 'INR',
    value: item.price ?? 0,
    items: [{ ...item, quantity: item.quantity ?? 1 }],
    ...devFlag,
  });
};

/**
 * begin_checkout — when user reaches the PaymentPage
 */
export const trackBeginCheckout = (items: GA4Item[], totalValue: number) => {
  if (!GA_TRACKING_ID) return;
  const utmParams = getStoredUTMParameters();
  gtag('event', 'begin_checkout', {
    currency: 'INR',
    value: totalValue,
    items,
    ...utmParams,
    ...devFlag,
  });
};

/**
 * add_payment_info — when user selects a payment method
 */
export const trackAddPaymentInfo = (
  items: GA4Item[],
  totalValue: number,
  paymentType: string
) => {
  if (!GA_TRACKING_ID) return;
  gtag('event', 'add_payment_info', {
    currency: 'INR',
    value: totalValue,
    payment_type: paymentType,
    items,
    ...devFlag,
  });
};

/**
 * purchase — fired on the PaymentSuccessPage (the most important event)
 */
export const trackPurchase = (params: {
  transactionId: string;
  value: number;
  items: GA4Item[];
  paymentMethod?: string;
  coupon?: string;
  tax?: number;
}) => {
  console.log("=== PURCHASE EVENT ===");
  if (!GA_TRACKING_ID) return;
  const utmParams = getStoredUTMParameters();
   console.log("=== PURCHASE EVENT 111 ===");

    console.log("GA_TRACKING_ID:", GA_TRACKING_ID);

    console.log("Payload:", params);

    if (!GA_TRACKING_ID) {

        console.log("GA_TRACKING_ID missing");

        return;

    }
  gtag('event', 'purchase', {
    transaction_id: params.transactionId,
    value: params.value,
    currency: 'INR',
    tax: params.tax ?? 0,
    coupon: params.coupon,
    payment_type: params.paymentMethod,
    items: params.items,
    ...utmParams,
    ...devFlag,
  });
  console.log("Purchase sent to GA4:");
};

/**
 * Shortcut used by existing code – wraps trackPurchase for a single service booking
 */
export const trackServiceBooking = (
  serviceId: string,
  serviceName: string,
  amount?: number
) => {
  trackConversion('service_booking', amount, {
    service_id: serviceId,
    service_name: serviceName,
  });
};

// ─── User Events ─────────────────────────────────────────────────────────────

export const trackUserRegistration = (method: string = 'oauth') => {
  if (!GA_TRACKING_ID) return;
  const utmParams = getStoredUTMParameters();
  gtag('event', 'sign_up', {
    method,
    ...utmParams,
    ...devFlag,
  });
};

export const trackUserLogin = (method: string = 'oauth') => {
  if (!GA_TRACKING_ID) return;
  gtag('event', 'login', {
    method,
    ...devFlag,
  });
};

/**
 * Set user_id in GA4 so all events from this user are linked.
 * Call this after a successful login/token exchange.
 */
export const setGAUser = (userId: string) => {
  if (!GA_TRACKING_ID) return;
  gtag('config', GA_TRACKING_ID, {
    user_id: userId,
  });
};

/**
 * Clear user_id on logout.
 */
export const clearGAUser = () => {
  if (!GA_TRACKING_ID) return;
  gtag('config', GA_TRACKING_ID, {
    user_id: undefined,
  });
};

// ─── UI Interaction Helpers ───────────────────────────────────────────────────

export const trackFormSubmission = (formName: string, success: boolean = true) => {
  trackEventWithUTM(
    success ? 'form_submit_success' : 'form_submit_error',
    'form',
    formName
  );
};

export const trackButtonClick = (buttonName: string, section?: string) => {
  trackEventWithUTM(
    'click',
    'button',
    `${section ? `${section}_` : ''}${buttonName}`
  );
};

export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  if (!GA_TRACKING_ID) return;
  gtag('event', 'search', {
    search_term: searchTerm,
    results_count: resultsCount,
    ...devFlag,
  });
};

export const trackExternalLink = (url: string, linkText?: string) => {
  trackEventWithUTM('click', 'external_link', linkText || url, {
    destination_url: url,
  });
};

export const trackFileDownload = (fileName: string, fileType?: string) => {
  trackEventWithUTM('download', 'file', fileName, {
    file_name: fileName,
    file_type: fileType,
  });
};

// ─── Diagnostics ─────────────────────────────────────────────────────────────

export const isGALoaded = (): boolean => {
  return typeof window !== 'undefined' &&
    typeof window.gtag === 'function' &&
    Boolean(GA_TRACKING_ID);
};

export const logGAStatus = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[GA4] Status:', {
      isLoaded: isGALoaded(),
      trackingId: GA_TRACKING_ID,
      utmParams: getUTMParameters(),
    });
  }
};


